import { api } from '@/api';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { CreateOrUpdateUnitDto, ErrorHandlerFunction, UnitDto } from '@/api/generator';
import { SelectItemTypeObject } from '@/components/forms/types';
import { keyBy, get, throttle } from 'lodash';

export const useUnitsStore = defineStore('units', () => {
  const units = ref<UnitDto[]>();
  const unitsDict = computed(() => keyBy(units.value, 'id'));

  async function update() {
    const { result } = await api.unitsApi.getUnitsCatalog();
    if (result) {
      units.value = result;
    }
  }

  async function initialize() {
    if (!units.value) {
      await update();
    }
  }

  const options = computed<SelectItemTypeObject<UnitDto>[]>(
    () =>
      units.value?.map((unit) => ({
        title: unit.abbreviation,
        value: unit,
      })) || [],
  );

  async function deleteUnit(id: string) {
    const deleteRes = await api.unitsApi.deleteUnit({
      id,
    });
    await update();
    return deleteRes;
  }

  async function updateUnit(
    id: string,
    unit: CreateOrUpdateUnitDto,
    errorHandler: ErrorHandlerFunction[],
  ) {
    const updateRes = await api.unitsApi.updateUnit(
      {
        id: id,
        createOrUpdateUnitDto: unit,
      },
      errorHandler,
    );

    await update();
    return updateRes;
  }

  async function addUnit(unit: CreateOrUpdateUnitDto, errorHandler: ErrorHandlerFunction[]) {
    const createRes = await api.unitsApi.createUnit(
      {
        createOrUpdateUnitDto: unit,
      },
      errorHandler,
    );
    await update();

    return createRes;
  }

  function getById(id?: string | null) {
    return id ? get(unitsDict.value, id, undefined) : undefined;
  }

  return {
    initialize: throttle(initialize, 500),
    update,
    getById,
    addUnit,
    updateUnit,
    deleteUnit,
    options,
    units,
  };
});
